import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { getEnvironmentInfo } from '../../_util/environment-info';

/**
 * Two small pills that always show which CLIENT build (DEV/PROD) is running and which DATABASE it
 * is connected to. DEV is amber, PROD is neutral, so a DEV client or DB never blends in.
 * `compact` drops the word labels (icon + value only) for narrow mobile headers.
 * Both values are fixed for the lifetime of the page (a database switch reloads the app).
 */
@Component({
  selector: 'app-env-badges',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      type="button"
      class="env-badges"
      [class.env-badges--compact]="compact"
      [attr.aria-label]="'Environment: ' + info.summary + '. Open app info.'"
      [title]="info.summary + ' — click for app info'"
      (click)="activate.emit()">
      <span class="env-badge" [class.env-badge--dev]="info.client === 'DEV'">
        <i class="pi pi-desktop" aria-hidden="true"></i>
        @if (!compact) { <span class="env-badge__label">Client</span> }
        <span class="env-badge__value">{{ info.client }}</span>
      </span>
      <span class="env-badge" [class.env-badge--dev]="info.database === 'DEV'">
        <i class="pi pi-database" aria-hidden="true"></i>
        @if (!compact) { <span class="env-badge__label">DB</span> }
        <span class="env-badge__value">{{ info.database }}</span>
      </span>
      @if (info.warning) {
        <i class="pi pi-exclamation-triangle env-badges__warn" aria-hidden="true"></i>
      }
    </button>
  `,
  styles: `
    :host { display: inline-flex; }
    .env-badges {
      display: inline-flex; align-items: center; gap: 4px;
      background: transparent; border: 0; padding: 0; cursor: pointer; font: inherit;
      min-height: 44px; /* comfortable touch target; the pills themselves stay small */
    }
    .env-badge {
      display: inline-flex; align-items: center; gap: 4px;
      padding: 3px 8px; border-radius: 999px; white-space: nowrap;
      font: 700 11px/1 system-ui, sans-serif; letter-spacing: 0.04em;
      background: var(--p-surface-200, #e2e8f0); color: var(--p-surface-800, #1e293b);
    }
    :host-context(.p-dark) .env-badge { background: var(--p-surface-700, #334155); color: var(--p-surface-0, #fff); }
    .env-badge--dev, :host-context(.p-dark) .env-badge--dev { background: #f59e0b; color: #1a1200; }
    .env-badge__label { font-weight: 500; opacity: 0.8; text-transform: uppercase; }
    .env-badges--compact { gap: 3px; }
    .env-badges--compact .env-badge { padding: 3px 6px; font-size: 10px; gap: 3px; }
    .env-badges__warn { color: #f59e0b; font-size: 14px; }
  `,
})
export class EnvBadgesComponent {
  @Input() compact = false;
  /** Emitted on click/tap; the host opens the App Info dialog. */
  @Output() activate = new EventEmitter<void>();

  readonly info = getEnvironmentInfo();
}
